/* eslint-disable perfectionist/sort-objects */

import { ApplicationEnum } from "~/@types/enums";
import {
	featurePageMapping as availabilityFeaturePageMapping,
	flatPageMapping as availabilityFlatPageMapping
} from "~/locationMaps/availabilityLocationMaps";
import {
	featurePageMapping as benchmarkFeaturePageMapping,
	flatPageMapping as benchmarkFlatPageMapping
} from "~/locationMaps/benchmarkLocationMaps";
import {
	featurePageMapping as countFeaturePageMapping,
	flatPageMapping as countFlatPageMapping
} from "~/locationMaps/countLocationMaps";
import {
	featurePageMapping as engageFeaturePageMapping,
	flatPageMapping as engageFlatPageMapping
} from "~/locationMaps/engageLocationMaps";
import {
	featurePageMapping as eventsFeaturePageMapping,
	flatPageMapping as eventsFlatPageMapping
} from "~/locationMaps/eventsLocationMaps";
import {
	featurePageMapping as forecastFeaturePageMapping,
	flatPageMapping as forecastFlatPageMapping
} from "~/locationMaps/forecastLocationMaps";
import {
	featurePageMapping as fulfillFeaturePageMapping,
	flatPageMapping as fulfillFlatPageMapping
} from "~/locationMaps/fulfillLocationMaps";
import {
	featurePageMapping as integrationsFeaturePageMapping,
	flatPageMapping as integrationsFlatPageMapping
} from "~/locationMaps/integrationsLocationMaps";
import {
	featurePageMapping as intelligenceFeaturePageMapping,
	flatPageMapping as intelligenceFlatPageMapping
} from "~/locationMaps/intelligenceLocationMaps";
import {
	featurePageMapping as materialFeaturePageMapping,
	flatPageMapping as materialFlatPageMapping
} from "~/locationMaps/materialLocationMaps";
import {
	featurePageMapping as portalFeaturePageMapping,
	flatPageMapping as portalFlatPageMapping
} from "~/locationMaps/portalLocationMaps";
import {
	featurePageMapping as produceFeaturePageMapping,
	flatPageMapping as produceFlatPageMapping
} from "~/locationMaps/produceLocationMaps";
import {
	featurePageMapping as restockFeaturePageMapping,
	flatPageMapping as restockFlatPageMapping
} from "~/locationMaps/restockLocationMaps";
import {
	featurePageMapping as verifyFeaturePageMapping,
	flatPageMapping as verifyFlatPageMapping
} from "~/locationMaps/verifyLocationMaps";
import {
	featurePageMapping as worksuiteFeaturePageMapping,
	flatPageMapping as worksuiteFlatPageMapping
} from "~/locationMaps/worksuiteLocationMaps";

const flatPageMaps: { [key in ApplicationEnum]?: { [key: string]: PageMapping } } = {
	availability: availabilityFlatPageMapping,
	benchmark: benchmarkFlatPageMapping,
	count: countFlatPageMapping,
	engage: engageFlatPageMapping,
	events: eventsFlatPageMapping,
	forecast: forecastFlatPageMapping,
	fulfill: fulfillFlatPageMapping,
	intelligence: intelligenceFlatPageMapping,
	material: materialFlatPageMapping,
	portal: portalFlatPageMapping,
	produce: produceFlatPageMapping,
	restock: restockFlatPageMapping,
	verify: verifyFlatPageMapping,
	worksuite: { ...worksuiteFlatPageMapping, ...integrationsFlatPageMapping }
};

const featurePageMaps: { [key in ApplicationEnum]?: { [key: string]: PageMapping } } = {
	availability: availabilityFeaturePageMapping,
	benchmark: benchmarkFeaturePageMapping,
	count: countFeaturePageMapping,
	engage: engageFeaturePageMapping,
	events: eventsFeaturePageMapping,
	forecast: forecastFeaturePageMapping,
	fulfill: fulfillFeaturePageMapping,
	intelligence: intelligenceFeaturePageMapping,
	material: materialFeaturePageMapping,
	portal: portalFeaturePageMapping,
	produce: produceFeaturePageMapping,
	restock: restockFeaturePageMapping,
	verify: verifyFeaturePageMapping,
	worksuite: { ...worksuiteFeaturePageMapping, ...integrationsFeaturePageMapping }
};

const defaultPageMapping: { [key: string]: PageMapping } = {
	"/": {
		nav: true,
		title: "",
		description: "",
		label: "Home",
		labelKey: "nav.system.home.label",
		buttons: [],
		actions: [],
		icon: "home"
	}
};

const helpPageMapping: { [key: string]: PageMapping } = {
	"/support/docs": {
		nav: true,
		title: "",
		description: "",
		label: "Documentation",
		labelKey: "nav.system.documentation.label",
		buttons: [],
		actions: [],
		icon: "open_in_new",
		linkOut: "https://docs.silverfern.com"
	},
	"/settings/error-log": {
		nav: true,
		title: "",
		description: "The last 100 errors that have occurred in the app are available here.",
		descriptionKey: "nav.system.errorLog.description",
		label: "Error Log",
		labelKey: "nav.system.errorLog.label",
		icon: "error",
		buttons: [],
		actions: []
	}
};

const accountPageMapping: { [key: string]: PageMapping } = {
	"/settings": {
		nav: true,
		title: "",
		description: "Manage app settings.",
		descriptionKey: "nav.system.settings.description",
		label: "Settings",
		labelKey: "nav.system.settings.label",
		icon: "settings",
		buttons: [],
		actions: []
	},
	"/settings/integrations": {
		nav: true,
		title: "",
		description: "Manage external integrations.",
		descriptionKey: "nav.system.integrations.description",
		label: "Integrations",
		labelKey: "nav.system.integrations.label",
		icon: "hub",
		buttons: [],
		actions: []
	},
	"/settings/users": {
		nav: true,
		title: "",
		description: "Manage users and their roles.",
		descriptionKey: "nav.system.users.description",
		label: "Users",
		labelKey: "nav.system.users.label",
		icon: "group",
		buttons: [],
		actions: []
	},
	"/settings/data-management": {
		nav: true,
		title: "",
		description: "Review and clear or combine records Work Suite thinks might be duplicates.",
		descriptionKey: "nav.system.dataManagement.description",
		label: "Data",
		labelKey: "nav.system.dataManagement.label",
		icon: "fact_check",
		buttons: [],
		actions: []
	}
};

const insightsPageMapping: { [key: string]: PageMapping } = {
	"/intelligence": {
		nav: true,
		title: "",
		description: "Business intelligence and analytics.",
		descriptionKey: "nav.system.intelligence.description",
		label: "Intelligence",
		labelKey: "nav.system.intelligence.label",
		icon: "insights",
		buttons: [],
		actions: []
	},
	"/benchmark": {
		nav: true,
		title: "",
		description: "Compare company performance across your peer community.",
		descriptionKey: "nav.system.benchmark.description",
		label: "Benchmark",
		labelKey: "nav.system.benchmark.label",
		icon: "leaderboard",
		buttons: [],
		actions: []
	}
};

const systemPageMapping: { [key: string]: PageMapping } = {
	"/my-proposal": {
		nav: true,
		title: "My Proposal",
		titleKey: "nav.system.myProposal.title",
		description: "View your personalized pricing proposal.",
		descriptionKey: "nav.system.myProposal.description",
		label: "My Proposal",
		labelKey: "nav.system.myProposal.label",
		icon: "sell",
		buttons: [],
		actions: []
	},
	"/notifications": {
		nav: false,
		title: "Notifications",
		titleKey: "nav.system.notifications.title",
		description: "Your notifications, newest first.",
		descriptionKey: "nav.system.notifications.description",
		label: "Notifications",
		labelKey: "nav.system.notifications.label",
		buttons: [],
		actions: []
	},
	"/reports": {
		nav: true,
		title: "Reports",
		titleKey: "nav.system.reports.title",
		description: "Create and manage reports.",
		descriptionKey: "nav.system.reports.description",
		label: "Reports",
		labelKey: "nav.system.reports.label",
		icon: "assessment",
		buttons: [],
		actions: []
	},
	"/reports/executions": {
		nav: false,
		title: "Execution History",
		titleKey: "nav.system.reportExecutions.title",
		description: "View past report runs and their outcomes.",
		descriptionKey: "nav.system.reportExecutions.description",
		label: "Execution History",
		labelKey: "nav.system.reportExecutions.label",
		buttons: [],
		actions: []
	},
	"/reports/email-issues": {
		nav: false,
		title: "Email Issues",
		titleKey: "nav.system.emailIssues.title",
		description: "Emails from the last 90 days that failed to deliver.",
		descriptionKey: "nav.system.emailIssues.description",
		label: "Email Issues",
		labelKey: "nav.system.emailIssues.label",
		buttons: [],
		actions: []
	},
	"/reports/schedules": {
		nav: false,
		title: "Report Schedules",
		titleKey: "nav.system.reportSchedules.title",
		description: "Manage scheduled report deliveries.",
		descriptionKey: "nav.system.reportSchedules.description",
		label: "Schedules",
		labelKey: "nav.system.reportSchedules.label",
		buttons: [],
		actions: []
	},
	"/reports/templates": {
		nav: false,
		title: "Template Library",
		titleKey: "nav.system.reportTemplates.title",
		description: "Browse and select report templates.",
		descriptionKey: "nav.system.reportTemplates.description",
		label: "Templates",
		labelKey: "nav.system.reportTemplates.label",
		buttons: [],
		actions: []
	},
	"/reports/order-emails": {
		nav: false,
		title: "Order Emails",
		titleKey: "nav.system.orderEmails.title",
		description: "Set the recipients, subject and body of order confirmation and acknowledgment emails.",
		descriptionKey: "nav.system.orderEmails.description",
		label: "Order Emails",
		labelKey: "nav.system.orderEmails.label",
		buttons: [],
		actions: []
	},
	"/insights": {
		nav: true,
		title: "",
		description: "Business insights, intelligence, and activity.",
		descriptionKey: "nav.system.insights.description",
		label: "Insights",
		labelKey: "nav.system.insights.label",
		icon: "insights",
		buttons: [],
		actions: [],
		children: insightsPageMapping
	},
	"/administration": {
		nav: true,
		title: "",
		description: "Manage account settings, integrations, and activity.",
		descriptionKey: "nav.system.administration.description",
		label: "Administration",
		labelKey: "nav.system.administration.label",
		icon: "manage_accounts",
		buttons: [],
		actions: [],
		children: accountPageMapping
	},
	"/support": {
		nav: true,
		title: "",
		description: "Get help with the app.",
		descriptionKey: "nav.system.support.description",
		label: "Help",
		labelKey: "nav.system.support.label",
		icon: "contact_support",
		buttons: [],
		actions: [],
		children: helpPageMapping
	},
	"/Forbidden": {
		nav: false,
		title: "Not Authorized",
		titleKey: "nav.system.forbidden.title",
		description: "You could not be authorized to view the requested page.",
		descriptionKey: "nav.system.forbidden.description",
		label: "Forbidden",
		labelKey: "nav.system.forbidden.label",
		buttons: [],
		actions: []
	},
	"/account": {
		nav: true,
		title: "",
		description: "View account settings.",
		descriptionKey: "nav.system.profile.description",
		label: "Profile",
		labelKey: "nav.system.profile.label",
		icon: "account_circle",
		buttons: [],
		actions: []
	},
	"/settings/preferences": {
		nav: false,
		title: "",
		description: "",
		label: "Preferences",
		labelKey: "accountMenu.preferences",
		buttons: [],
		actions: []
	},
	"/settings/shortcuts": {
		nav: false,
		title: "",
		description: "",
		label: "Shortcuts",
		labelKey: "preferences.tabs.shortcuts",
		buttons: [],
		actions: []
	},
	"/settings/notifications": {
		nav: false,
		title: "",
		description: "",
		label: "Notification Settings",
		labelKey: "nav.system.notificationSettings.label",
		buttons: [],
		actions: []
	},
	"/settings/app-installs": {
		nav: false,
		title: "",
		description: "",
		label: "App Installs",
		labelKey: "accountMenu.appInstalls",
		buttons: [],
		actions: []
	},
	"/my-commissions": {
		nav: false,
		title: "",
		description: "",
		label: "My Commissions",
		labelKey: "accountMenu.myCommissions",
		buttons: [],
		actions: []
	}
};

const getFeaturePageMapping = (path: ApplicationEnum): { [key: string]: PageMapping } => {
	if (Object.prototype.hasOwnProperty.call(featurePageMaps, path)) {
		return featurePageMaps[path]!;
	}
	return { ...defaultPageMapping };
};

const getFlatPageMapping = (path: ApplicationEnum): { [key: string]: PageMapping } => {
	if (Object.prototype.hasOwnProperty.call(flatPageMaps, path)) {
		return { ...flatPageMaps[path], ...systemPageMapping };
	}
	return { ...defaultPageMapping, ...systemPageMapping };
};

const flattenNavPageLinks = (mapping: { [key: string]: PageMapping }, acc: PageLink[] = []): PageLink[] => {
	for (const [path, page] of Object.entries(mapping)) {
		if (page.nav && page.label) {
			acc.push({
				icon: page.icon,
				label: page.title || page.label,
				labelKey: page.title ? page.titleKey : page.labelKey,
				path
			});
		}
		if (page.children) {
			flattenNavPageLinks(page.children, acc);
		}
	}
	return acc;
};

const getSectionKey = (path: string, flatPageMapping: { [key: string]: PageMapping }): string | undefined =>
	Object.keys(flatPageMapping).find((key) => new RegExp(`${key}$`).test(path));

const getPageInfo = (path: string, flatPageMapping: { [key: string]: PageMapping }): PageMapping | undefined => {
	const match = getSectionKey(path, flatPageMapping);
	return match ? flatPageMapping[match] : undefined;
};

const containsPage = (mapping: PageMapping, path: string): boolean => {
	if (!mapping.children) return false;
	return Object.entries(mapping.children).some(
		([key, child]) => new RegExp(`${key}$`).test(path) || containsPage(child, path)
	);
};

const getSectionIcon = (path: string, featurePageMapping: { [key: string]: PageMapping }): string | undefined => {
	const sectionKeys = Object.keys(featurePageMapping);
	const navSection = sectionKeys
		.filter((key) => containsPage(featurePageMapping[key], path))
		.sort((a, b) => b.length - a.length)[0];
	const prefixSection = sectionKeys
		.filter((key) => path === key || path.startsWith(`${key}/`))
		.sort((a, b) => b.length - a.length)[0];
	const section = navSection ?? prefixSection;
	return section ? featurePageMapping[section].icon : undefined;
};

export {
	flattenNavPageLinks,
	getFeaturePageMapping,
	getFlatPageMapping,
	getPageInfo,
	getSectionIcon,
	getSectionKey,
	systemPageMapping
};
